import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Users,
  Play,
  Copy,
  QrCode,
  Settings,
  Shuffle,
  ArrowLeft,
  Crown,
  UserPlus,
  Loader2,
  CheckCircle2
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import api from '@/lib/api';
import QRCode from 'react-qr-code';

const BACKEND_URL = window.location.origin;

export default function QuizLiveLobby() {
  const { sessionCode } = useParams();
  const navigate = useNavigate();
  const wsRef = useRef(null);
  
  const [session, setSession] = useState(null);
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const [connected, setConnected] = useState(false);
  const [showQR, setShowQR] = useState(false);

  const joinUrl = `${window.location.origin}/quiz-live/jogar/${sessionCode}`;

  useEffect(() => {
    loadSession();
    connectWebSocket();

    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, [sessionCode]);

  const loadSession = async () => {
    try {
      const response = await api.get(`/quiz-live/sessions/${sessionCode}`);
      setSession(response.data);
      setPlayers(response.data.players || []);
    } catch (error) {
      console.error('Error loading session:', error);
      toast.error('Sessão não encontrada');
      navigate('/quiz-live');
    } finally {
      setLoading(false);
    }
  };

  const connectWebSocket = () => {
    const wsUrl = BACKEND_URL.replace('https://', 'wss://').replace('http://', 'ws://');
    const ws = new WebSocket(`${wsUrl}/api/quiz-live/ws/host/${sessionCode}`);
    
    ws.onopen = () => {
      console.log('WebSocket connected');
      setConnected(true);
    };
    
    ws.onmessage = (event) => {
      const data = JSON.parse(event.data);
      console.log('WS message:', data);
      
      if (data.type === 'player_connected') {
        setPlayers(prev => {
          const exists = prev.some(p => p.id === data.player.id);
          if (exists) return prev;
          return [...prev, data.player];
        });
        toast.success(`${data.player.name} entrou!`, {
          icon: <UserPlus className="w-4 h-4" />
        });
      }
      
      if (data.type === 'player_disconnected') {
        setPlayers(prev => prev.filter(p => p.id !== data.player_id));
      }
      
      if (data.type === 'game_started') {
        navigate(`/quiz-live/host/${sessionCode}`);
      }
    };
    
    ws.onclose = () => {
      console.log('WebSocket disconnected');
      setConnected(false);
    };
    
    ws.onerror = (error) => {
      console.error('WebSocket error:', error);
    };
    
    wsRef.current = ws;
    
    // Keep-alive ping
    const pingInterval = setInterval(() => {
      if (ws.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify({ type: 'ping' }));
      }
    }, 30000);
    
    return () => clearInterval(pingInterval);
  };

  const handleCopyCode = () => {
    navigator.clipboard.writeText(sessionCode);
    toast.success('Código copiado!');
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(joinUrl);
    toast.success('Link copiado!');
  };

  const handleStartGame = async () => {
    if (players.length === 0) {
      toast.error('Aguarde pelo menos 1 jogador entrar');
      return;
    }
    
    setStarting(true);
    try {
      await api.post(`/quiz-live/sessions/${sessionCode}/start`);
      // Navigation will be handled by WebSocket
    } catch (error) {
      console.error('Error starting game:', error);
      toast.error('Erro ao iniciar jogo');
      setStarting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-purple-950 to-slate-950 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-white animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-purple-950 to-slate-950">
      {/* Header */}
      <div className="bg-slate-900/80 border-b border-white/10 sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/quiz-live')}
              className="text-white"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-xl font-bold text-white">{session?.quiz_title}</h1>
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="text-xs border-green-500 text-green-400">
                  <span className="w-2 h-2 bg-green-500 rounded-full mr-1 animate-pulse" />
                  {connected ? 'Conectado' : 'Conectando...'}
                </Badge>
                <Badge variant="outline" className="text-xs border-white/30 text-gray-300">
                  {session?.mode === 'teams' ? 'Equipes' : 'Individual'}
                </Badge>
              </div>
            </div>
          </div>
          
          <Button
            variant="ghost"
            size="icon"
            className="text-white"
          >
            <Settings className="w-5 h-5" />
          </Button>
        </div>
      </div>

      <div className="max-w-6xl mx-auto p-4 lg:p-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Join Info */}
          <div className="lg:col-span-1 space-y-4">
            {/* Session Code Card */}
            <Card className="bg-gradient-to-br from-purple-600 to-purple-800 border-0 overflow-hidden">
              <CardContent className="p-6 text-center">
                <p className="text-white/80 text-sm mb-2">Código da Sessão</p>
                <div 
                  className="text-5xl lg:text-6xl font-black text-white tracking-wider cursor-pointer hover:scale-105 transition-transform"
                  onClick={handleCopyCode}
                  data-testid="session-code"
                >
                  {sessionCode}
                </div>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={handleCopyCode}
                  className="mt-4 bg-white/20 hover:bg-white/30 text-white"
                >
                  <Copy className="w-4 h-4 mr-2" />
                  Copiar código
                </Button>
              </CardContent>
            </Card>

            {/* QR Code Card */}
            <Card className="bg-white/5 border-white/10">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <p className="text-white font-medium">QR Code</p>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowQR(!showQR)}
                    className="text-gray-400"
                  >
                    <QrCode className="w-4 h-4 mr-2" />
                    {showQR ? 'Ocultar' : 'Mostrar'}
                  </Button>
                </div>
                
                {showQR && (
                  <div className="bg-white p-4 rounded-xl">
                    <QRCode
                      value={joinUrl}
                      size={200}
                      className="w-full h-auto"
                    />
                  </div>
                )}
                
                <div className="mt-4">
                  <p className="text-xs text-gray-400 mb-2">Link de acesso:</p>
                  <div className="flex gap-2">
                    <code className="flex-1 bg-white/10 rounded px-3 py-2 text-xs text-gray-300 truncate">
                      {joinUrl}
                    </code>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={handleCopyLink}
                      className="text-white"
                    >
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Start Button */}
            <Button
              onClick={handleStartGame}
              disabled={starting || players.length === 0}
              className="w-full h-14 text-lg bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 disabled:opacity-50"
              data-testid="start-game-btn"
            >
              {starting ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Iniciando...
                </>
              ) : (
                <>
                  <Play className="w-5 h-5 mr-2" />
                  Iniciar Jogo ({players.length} {players.length === 1 ? 'jogador' : 'jogadores'})
                </>
              )}
            </Button>
          </div>

          {/* Right Column - Players List */}
          <div className="lg:col-span-2">
            <Card className="bg-white/5 border-white/10 h-full">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-purple-600 flex items-center justify-center">
                      <Users className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-white">Jogadores</h3>
                      <p className="text-sm text-gray-400">
                        {players.length} de {session?.max_players || 50} conectados
                      </p>
                    </div>
                  </div>
                  
                  {session?.mode === 'teams' && players.length >= 4 && (
                    <Button variant="outline" className="border-white/20 text-white">
                      <Shuffle className="w-4 h-4 mr-2" />
                      Formar Equipes
                    </Button>
                  )}
                </div>

                {players.length === 0 ? (
                  <div className="text-center py-16">
                    <div className="w-20 h-20 rounded-full bg-white/10 flex items-center justify-center mx-auto mb-4">
                      <UserPlus className="w-10 h-10 text-gray-400" />
                    </div>
                    <h4 className="text-lg font-medium text-white mb-2">
                      Aguardando jogadores
                    </h4>
                    <p className="text-gray-400 max-w-sm mx-auto">
                      Compartilhe o código <span className="text-purple-400 font-bold">{sessionCode}</span> com
                      os alunos para que eles entrem na sessão
                    </p>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                    {players.map((player, index) => (
                      <div
                        key={player.id}
                        className="bg-white/10 rounded-xl p-4 text-center animate-fade-in"
                        style={{ animationDelay: `${index * 50}ms` }}
                      >
                        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center mx-auto mb-2 text-white font-bold">
                          {player.name.charAt(0).toUpperCase()}
                        </div>
                        <p className="text-white font-medium text-sm truncate">
                          {player.name}
                        </p>
                        <div className="flex items-center justify-center gap-1 mt-1">
                          <CheckCircle2 className="w-3 h-3 text-green-400" />
                          <span className="text-xs text-green-400">Pronto</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes fade-in {
          from {
            opacity: 0;
            transform: scale(0.9);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }
        .animate-fade-in {
          animation: fade-in 0.3s ease-out forwards;
        }
      `}</style>
    </div>
  );
}
